// Web push for the companion dashboard (QA P0-1). Before this, web received ZERO notifications —
// firebase.js was auth-only and there was no service worker. This brings web to parity with the
// Flutter FCM spine using the SAME backend path: a web FCM registration token is POSTed to
// /auth/device-token (auth.js) and stored alongside mobile tokens in `fcmTokens`, so the existing
// notifications.js sendMulticast delivers to web with no backend change.
//
// Two delivery surfaces, mirroring mobile:
//   • foreground (tab focused) → onMessage here: force a manifest refresh so the UI updates
//     immediately, and surface notify-type pushes via the Notification API.
//   • background (tab hidden/closed) → public/firebase-messaging-sw.js handles it.
//
// Everything is gated on a VAPID key + browser support; if either is missing this no-ops quietly
// so auth and the rest of the dashboard are unaffected.
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';
import app, { firebaseConfig, VAPID_KEY } from '../firebase';
import { api, refreshManifest } from './api';

let started = false; // init once per page load even if the shell re-mounts

// Register the background worker, passing the public config via the query string so the worker
// (a separate context that can't read window._env_) initializes with the same project. The config
// values are not secrets — they identify the project; backend still verifies every ID token.
async function registerWorker() {
  if (!('serviceWorker' in navigator)) return null;
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
}

// The in-app route a push points at, with the deep-link tab hint. Kept in sync with the service
// worker's notificationclick handler and the mobile _tabForPush.
function targetForPush(d) {
  if (!d.momentId) return '/app';
  const tab = d.type === 'moment_capture_reminder' || d.reason === 'media' ? 'media'
    : d.reason === 'messages' ? 'messages' : '';
  return `/app/moments/${d.momentId}${tab ? `?tab=${tab}` : ''}`;
}

// Show a tray notification for a foreground push the SDK won't auto-display. Data-only (silent)
// syncs carry no title and just trigger the manifest refresh above.
function showForeground(payload) {
  const n = payload?.notification || {};
  const d = payload?.data || {};
  const title = n.title || d.title;
  if (!title || Notification.permission !== 'granted') return;
  // RT-4: don't banner the moment the user is already looking at (mirrors the Flutter
  // visibleMomentId suppression) — the open MomentDetail already updates live.
  if (d.momentId && window.location.pathname.endsWith(`/moments/${d.momentId}`)) return;
  try {
    const note = new Notification(title, {
      body: n.body || d.body || '', icon: '/spheresync-icon.png', tag: d.type || undefined, data: d,
    });
    // Parity with the background service worker: clicking a foreground banner deep-links to the
    // moment's Messages/Memories tab instead of doing nothing. (Foreground banners only show when
    // the tab is focused but NOT already on that moment, so a navigation here is always a move.)
    note.onclick = () => {
      window.focus();
      note.close();
      if (d.momentId) window.location.assign(targetForPush(d));
    };
  } catch { /* Notification constructor unavailable — refresh already happened */ }
}

export async function initWebPush() {
  if (started) return;
  started = true;
  try {
    if (!VAPID_KEY || !(await isSupported())) return; // unsupported browser or unconfigured env
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return; // user declined — leave web on the polling path only

    const registration = await registerWorker();
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration || undefined,
    });
    if (token) {
      // Same endpoint the mobile app uses; backend de-dupes and keeps the last 10 tokens.
      await api.post('/auth/device-token', { token, platform: 'web' }).catch(() => {});
    }

    // Foreground messages: refresh the near-real-time spine immediately, then surface a banner.
    onMessage(messaging, (payload) => {
      refreshManifest();
      showForeground(payload);
    });
  } catch (err) {
    // Push is best-effort; never let it break the dashboard. Polling (P0-2) still covers realtime.
    console.warn('Web push init skipped:', err?.message || err);
    started = false; // allow a retry on the next mount (e.g. permission granted later)
  }
}

// Sign-out teardown — call BEFORE Firebase signOut (the unregister call needs the account's
// still-valid ID token). Unbinds this browser's push token from the account server-side AND
// deletes it locally, so the browser stops receiving this account's notifications and the next
// login registers a fresh token for whoever signs in. Best-effort, mirrors the mobile app.
export async function teardownWebPush() {
  try {
    if (!VAPID_KEY || !(await isSupported())) return;
    if (Notification.permission !== 'granted') return; // never registered
    const messaging = getMessaging(app);
    const registration = await navigator.serviceWorker?.getRegistration('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration || undefined,
    }).catch(() => null);
    if (token) {
      await api.delete('/auth/device-token', { data: { token } }).catch(() => {});
    }
    await deleteToken(messaging).catch(() => {});
  } catch {
    // Teardown must never block sign-out; the backend's exclusive claim on the next
    // register is the backstop.
  } finally {
    started = false; // a later sign-in on this page re-inits and registers fresh
  }
}
